import { useState } from "react";

import { startConsent } from "@/lib/api";

// Creator claim modal — used both per-skill (skill page) and in bulk (Official → publisher).
// `bulk` applies the chosen action to every imported skill under the same GitHub owner.
export default function ClaimModal({
  slug,
  bulk = false,
  onClose,
}: {
  slug: string;
  bulk?: boolean;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState<"consent" | "takedown" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [maintain, setMaintain] = useState(false);

  async function onClaim(intent: "consent" | "takedown") {
    setBusy(intent);
    try {
      const { authorize_url } = await startConsent(slug, intent, bulk, intent === "consent" && maintain);
      window.location.href = authorize_url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn’t start GitHub verification");
      setBusy(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] overflow-y-auto bg-black/75 backdrop-blur-sm"
      onClick={() => busy === null && onClose()}
    >
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
        <div
          className="w-full max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-[#111] shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Hero */}
          <div className="relative border-b border-white/10 bg-gradient-to-b from-primary/[0.08] to-transparent px-7 pt-8 pb-7">
            <button
              onClick={() => busy === null && onClose()}
              className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-lg text-text/40 transition hover:bg-white/10 hover:text-text"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            <h3 className="mt-4 font-display text-xl font-medium tracking-tight text-text">
              {bulk ? "Your skills are on SkillPatch" : "Your work is on SkillPatch"}
            </h3>
            <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-text/60">
              SkillPatch is the registry where developers discover and install skills for Claude and
              other AI agents. We found {bulk ? "these in your public repositories" : "this one in your public repository"} and
              listed {bulk ? "them" : "it"} under {bulk ? "their" : "its"} open-source license — always
              crediting you and linking back to the source. Now the whole community can find, install,
              and build on {bulk ? "them" : "it"}.
            </p>
          </div>

          {/* Body */}
          <div className="px-7 py-6">
            <p className="text-sm text-text/55">
              If you’re the author, take control in seconds. We verify you through GitHub — there’s no
              account to create, and we never post or change anything on your behalf.
            </p>

            <div className="mt-5 grid gap-3.5 sm:grid-cols-2">
              {/* Consent */}
              <div className="flex flex-col rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-green-500/30 bg-green-500/10 text-green-400">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></svg>
                </div>
                <h4 className="mt-3 font-display text-base font-medium">Give consent</h4>
                <p className="mt-1.5 flex-1 text-xs leading-relaxed text-text/50">
                  Add a verified <span className="text-text/70">“claimed”</span> badge so every
                  developer who finds {bulk ? "your skills knows they come" : "this skill knows it comes"} straight from you.
                </p>
                <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs text-text/70">
                  <input
                    type="checkbox"
                    checked={maintain}
                    onChange={(e) => setMaintain(e.target.checked)}
                    disabled={busy !== null}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-primary"
                  />
                  <span>
                    I want to maintain {bulk ? "them" : "it"}
                    <span className="mt-0.5 block text-[11px] text-text/40">
                      Become the owner — you’ll log in or sign up to finish.
                    </span>
                  </span>
                </label>
                <button
                  onClick={() => onClaim("consent")}
                  disabled={busy !== null}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-[12px] bg-primary px-4 py-2.5 font-display text-sm font-medium text-white shadow-btn transition-all duration-200 hover:bg-primary-hover hover:shadow-[0_0_24px_rgba(225,100,46,0.45)] active:scale-[0.98] disabled:opacity-50"
                >
                  {busy === "consent" ? "Redirecting…" : maintain ? (bulk ? "Claim & maintain all" : "Claim & maintain") : bulk ? "Consent to all" : "Give consent"}
                </button>
              </div>

              {/* Takedown */}
              <div className="flex flex-col rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-500/30 bg-red-500/10 text-red-400">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                </div>
                <h4 className="mt-3 font-display text-base font-medium">Request takedown</h4>
                <p className="mt-1.5 flex-1 text-xs leading-relaxed text-text/50">
                  Would rather {bulk ? "they weren’t" : "it wasn’t"} listed? Verify {bulk ? "they’re" : "it’s"} yours
                  and we’ll remove {bulk ? "them" : "it"} from SkillPatch immediately.
                </p>
                <button
                  onClick={() => onClaim("takedown")}
                  disabled={busy !== null}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-[12px] border border-red-500/40 px-4 py-2.5 font-display text-sm font-medium text-red-400 transition-all duration-200 hover:bg-red-500/10 active:scale-[0.98] disabled:opacity-50"
                >
                  {busy === "takedown" ? "Redirecting…" : bulk ? "Take down all" : "Request takedown"}
                </button>
              </div>
            </div>

            {err && <p className="mt-4 text-center text-sm text-red-400">{err}</p>}

            <p className="mt-5 flex items-center justify-center gap-1.5 text-center text-[11px] text-text/35">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
              Ownership is verified through GitHub — we only confirm you control the source repository, nothing more.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
